/**
 * Explicit route table. Paths are the Base44 page names, so every bookmark
 * and emailed link (/EventDetail?id=…, /OrderConfirmation?orderId=…) keeps
 * working. Admin and accounting screens load as separate chunks.
 */

import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";

import Layout from "@/Layout";
import PageNotFound from "@/lib/PageNotFound";
import { RequireAdmin, RequireAuth, RequireProfile, Spinner } from "@/auth/guards";
import ScrollToTop from "@/components/ScrollToTop";

import Home from "@/pages/Home";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import MyTickets from "@/pages/MyTickets";
import OrderConfirmation from "@/pages/OrderConfirmation";
import UserProfile from "@/pages/UserProfile";
import CompleteProfile from "@/pages/CompleteProfile";
import Login from "@/pages/auth/Login";
import SignUp from "@/pages/auth/SignUp";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import SetPassword from "@/pages/auth/SetPassword";

const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const ScanTickets = lazy(() => import("@/pages/ScanTickets"));
const Settings = lazy(() => import("@/pages/Settings"));
const Accounting = lazy(() => import("@/pages/Accounting"));
const Deposits = lazy(() => import("@/pages/Deposits"));
const DepositDetail = lazy(() => import("@/pages/DepositDetail"));
const Checking = lazy(() => import("@/pages/Checking"));
const ChartOfAccounts = lazy(() => import("@/pages/ChartOfAccounts"));
const AccountDetail = lazy(() => import("@/pages/AccountDetail"));
const Bills = lazy(() => import("@/pages/Bills"));
const PayBills = lazy(() => import("@/pages/PayBills"));
const Vendors = lazy(() => import("@/pages/Vendors"));
const PrintCheck = lazy(() => import("@/pages/PrintCheck"));
const ProfitAndLossReport = lazy(() => import("@/pages/ProfitAndLossReport"));
const BalanceSheet = lazy(() => import("@/pages/BalanceSheet"));
const Journal = lazy(() => import("@/pages/Journal"));
const Reconciliations = lazy(() => import("@/pages/Reconciliations"));
const CheckRegister = lazy(() => import("@/pages/CheckRegister"));
const ApAging = lazy(() => import("@/pages/ApAging"));

export function AppRoutes() {
  return (
    <Suspense fallback={<Spinner />}>
      <ScrollToTop />
      <Routes>
        <Route element={<Layout />}>
          <Route element={<RequireProfile />}>
            <Route path="/" element={<Home />} />
            <Route path="/Home" element={<Home />} />
            <Route path="/Events" element={<Events />} />
            <Route path="/EventDetail" element={<EventDetail />} />
            <Route path="/MyTickets" element={<MyTickets />} />
            <Route path="/Login" element={<Login />} />
            <Route path="/SignUp" element={<SignUp />} />
            <Route path="/VerifyEmail" element={<VerifyEmail />} />
            <Route path="/ForgotPassword" element={<ForgotPassword />} />
            <Route path="/ResetPassword" element={<ResetPassword />} />
            <Route path="/SetPassword" element={<SetPassword />} />

            <Route element={<RequireAuth />}>
              <Route path="/OrderConfirmation" element={<OrderConfirmation />} />
              <Route path="/UserProfile" element={<UserProfile />} />
              <Route path="/CompleteProfile" element={<CompleteProfile />} />
            </Route>

            <Route element={<RequireAdmin />}>
              <Route path="/AdminDashboard" element={<AdminDashboard />} />
              <Route path="/ScanTickets" element={<ScanTickets />} />
              <Route path="/Settings" element={<Settings />} />
              <Route path="/Accounting" element={<Accounting />} />
              <Route path="/Deposits" element={<Deposits />} />
              <Route path="/DepositDetail" element={<DepositDetail />} />
              <Route path="/Checking" element={<Checking />} />
              <Route path="/ChartOfAccounts" element={<ChartOfAccounts />} />
              <Route path="/AccountDetail" element={<AccountDetail />} />
              <Route path="/Bills" element={<Bills />} />
              <Route path="/PayBills" element={<PayBills />} />
              <Route path="/Vendors" element={<Vendors />} />
              <Route path="/PrintCheck" element={<PrintCheck />} />
              <Route path="/ProfitAndLossReport" element={<ProfitAndLossReport />} />
              <Route path="/BalanceSheet" element={<BalanceSheet />} />
              <Route path="/Journal" element={<Journal />} />
              <Route path="/Reconciliations" element={<Reconciliations />} />
              <Route path="/CheckRegister" element={<CheckRegister />} />
              <Route path="/ApAging" element={<ApAging />} />
            </Route>
          </Route>
          <Route path="*" element={<PageNotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
